import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CssBaseline, Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeContextProvider } from './contexts/ThemeContext'; // ✅ NEW: Import ThemeContext
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import DevicesPage from './pages/DevicesPage';
import SpeedMonitoringPage from './pages/SpeedMonitoringPage';
import TelemetryPage from './pages/TelemetryPage';
import WorkOrdersPage from './pages/WorkOrdersPage';
import AnalyticsPage from './pages/AnalyticsPage';
import DailyMileagePage from './pages/DailyMileagePage';
import FleetManagementPage from './pages/FleetManagementPage';
import MapPage from './pages/MapPage';
import ActivityPage from './pages/ActivityPage';
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';
import VehicleRequestsPage from './pages/VehicleRequestsPage';
import CompanyRegisterPage from './pages/CompanyRegisterPage';
import UserManagementPage from './pages/UserManagementPage';
import AcceptInvitationPage from './pages/AcceptInvitationPage';
import DriversPage from './pages/Driverspage';
import TripPlaybackPage from './pages/TripPlaybackPage';
import GeofencingDashboard from './pages/GeofencingDashboard';
import CompanySettingsPage from './pages/CompanySettingsPage';

// ✅ Create Query Client with optimized settings
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: true,
      retry: 1,
    },
  },
});

// Main App Component
const AppContent: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register-company" element={<CompanyRegisterPage />} />
        <Route path="/accept-invitation/:token" element={<AcceptInvitationPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/map" element={<MapPage />} />
        <Route path="/vehicle-requests" element={<VehicleRequestsPage />} />      
        <Route path="/devices" element={<DevicesPage />} />
        <Route path="/drivers" element={<DriversPage />} />
        <Route path="/trip-playback" element={<TripPlaybackPage />} />
        <Route path="/fleet-management" element={<FleetManagementPage />} />
        <Route path="/geofencing" element={<GeofencingDashboard />} />
        <Route path="/activity" element={<ActivityPage />} />
        <Route path="/maintenance" element={<div>Maintenance Page - Coming Soon</div>} />
        <Route path="/messages" element={<div>Messages Page - Coming Soon</div>} />
        <Route path="/groups" element={<div>Groups Page - Coming Soon</div>} />
        <Route path="/admin" element={<div>Administration Page - Coming Soon</div>} />
        <Route path="/admin/users" element={<UserManagementPage />} />    
        <Route path="/admin/company" element={<CompanySettingsPage />} />
        <Route path="/daily-mileage" element={<DailyMileagePage />} />
        <Route path="/fill-ups" element={<div>Fill-ups Page - Coming Soon</div>} />
        <Route path="/trips" element={<div>Trips Page - Coming Soon</div>} />
        <Route path="/timesheet" element={<div>Daily Timesheet Page - Coming Soon</div>} />
        <Route path="/services" element={<div>Services Page - Coming Soon</div>} />
        <Route path="/expenses" element={<div>Other Expenses Page - Coming Soon</div>} />
        <Route path="/reminders" element={<div>Reminders Page - Coming Soon</div>} />
        <Route path="/inspections" element={<div>Vehicle Inspections Page - Coming Soon</div>} />
        <Route path="/issues" element={<div>Issues Page - Coming Soon</div>} />
        <Route path="/work-orders" element={<WorkOrdersPage />} />
        <Route path="/speed-monitoring" element={<SpeedMonitoringPage />} />
        <Route path="/telemetry" element={<TelemetryPage />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <CssBaseline />
      <AuthProvider>
        {/* ✅ ThemeContextProvider must be INSIDE AuthProvider */}
        <ThemeContextProvider>
          <NotificationProvider>
            <Router>
              <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
                <AppContent />
              </Box>
            </Router>
          </NotificationProvider>
        </ThemeContextProvider>
      </AuthProvider>
      <ReactQueryDevtools initialIsOpen={false} position="bottom" />
    </QueryClientProvider>
  );
};

export default App;